import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bug, Lightbulb, HelpCircle, Heart, MessageCircle, Upload, X, Loader2,
  CheckCircle2, Clock, AlertCircle, FileText, ImageIcon,
} from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { api } from "@/lib/api";
import { isPremium } from "@/lib/licensing";

// Sprint 8.F — Mitgestalten-Hauptseite
// Hybrid: Submit-Form + eigene Feedback-Liste mit Status-Tracking.
// Kontakt-Link-Feedback (Do10.09.2026, 18:10) — 5. Kategorie „Nachricht
// an Andreas" ergaenzt, damit /feedback-Alias auch als allgemeine
// Kontaktaufnahme dient. Andreas liest alle Feedbacks im Admin-Overview.

const CATEGORIES = [
  { key: "bug",      label: "Bug gefunden",         hint: "Etwas funktioniert nicht wie erwartet.", Icon: Bug,       color: "red"    },
  { key: "feature",  label: "Idee / Feature-Wunsch", hint: "Was sollten wir bauen?",              Icon: Lightbulb, color: "amber"  },
  { key: "question", label: "Frage zur Bedienung",  hint: "Wie funktioniert ...?",                Icon: HelpCircle, color: "blue"  },
  { key: "praise",   label: "Lob / hat funktioniert", hint: "Was gefällt dir besonders?",        Icon: Heart,     color: "emerald" },
  { key: "contact",  label: "Nachricht an Andreas", hint: "Persönliche Nachricht — landet direkt bei Andreas.", Icon: MessageCircle, color: "indigo" },
];

const STATUS_META = {
  open:         { label: "Offen",                Icon: Clock,        color: "text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800" },
  in_progress:  { label: "In Bearbeitung",       Icon: AlertCircle,  color: "text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40" },
  done:         { label: "Umgesetzt",            Icon: CheckCircle2, color: "text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40" },
  wontfix:      { label: "Nicht geplant",        Icon: X,            color: "text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800" },
};

const MAX_MESSAGE = 2000;
const MIN_MESSAGE = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function renderMarkdownPreview(text) {
  // Sehr leichte Markdown-Heuristik: **bold**, *italic*, `code`, > quote, [link](url)
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code style=\"background:#F1F5F9;padding:2px 4px;border-radius:3px;font-size:0.9em;\">$1</code>")
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, "<a href=\"$2\" target=\"_blank\" rel=\"noopener\" style=\"color:#4563BB;text-decoration:underline;\">$1</a>")
    .replace(/^&gt; (.+)$/gm, "<blockquote style=\"border-left:3px solid #CBD5E1;padding:2px 8px;color:#475569;margin:4px 0;\">$1</blockquote>")
    .replace(/\n/g, "<br/>");
  return html;
}

export default function Mitgestalten() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const preType = params.get("type");
  const preContext = params.get("context");

  const [category, setCategory] = useState(preType && CATEGORIES.find(c => c.key === preType) ? preType : "");
  const [message, setMessage] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [notifyOnFix, setNotifyOnFix] = useState(true);
  const [screenshotB64, setScreenshotB64] = useState(null);
  const [screenshotName, setScreenshotName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Eigene Feedbacks
  const [myList, setMyList] = useState([]);
  const [myListLoading, setMyListLoading] = useState(true);

  useEffect(() => {
    api
      .get("/feedback/my-list")
      .then((r) => setMyList(r.data?.items || []))
      .catch(() => setMyList([]))
      .finally(() => setMyListLoading(false));
  }, []);

  const messageLen = message.length;
  const valid = category && messageLen >= MIN_MESSAGE && messageLen <= MAX_MESSAGE;

  const handleFile = async (file) => {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Bitte nur Bild-Dateien hochladen (PNG/JPG/GIF).");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("Bild zu groß (max. 5 MB).");
      return;
    }
    try {
      const b64 = await fileToBase64(file);
      setScreenshotB64(b64);
      setScreenshotName(file.name);
    } catch (_e) {
      setError("Bild konnte nicht eingelesen werden.");
    }
  };

  const handleSubmit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    const context = {
      url: window.location.href,
      pre_context: preContext || null,
      user_agent: navigator.userAgent.slice(0, 200),
      screen: `${window.innerWidth}x${window.innerHeight}`,
      ts: new Date().toISOString(),
    };
    try {
      const r = await api.post("/feedback/submit", {
        type: category,
        message,
        screenshot_base64: screenshotB64,
        contact_email: contactEmail.trim() || null,
        notify_on_fix: notifyOnFix,
        context,
      });
      setSubmittedId(r.data?.feedback_id || "ok");
      setMessage("");
      setScreenshotB64(null);
      setScreenshotName("");
      // refresh list
      api.get("/feedback/my-list").then((res) => setMyList(res.data?.items || [])).catch(() => {});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "monthly_limit_reached") {
        setError("Du hast dein Free-Monatslimit (3 Feedbacks) erreicht. Mit Pro Solo unbegrenzt.");
      } else if (detail?.code === "message_length") {
        setError(`Bitte 10–${MAX_MESSAGE} Zeichen Nachricht.`);
      } else if (detail?.code === "screenshot_too_large") {
        setError("Screenshot ist zu groß (max. 5 MB).");
      } else if (detail?.code === "invalid_type") {
        setError("Ungültige Kategorie.");
      } else {
        setError("Senden fehlgeschlagen. Bitte später erneut.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCategory = useMemo(() => CATEGORIES.find(c => c.key === category), [category]);

  return (
    <RedesignLayout>
      {({ data }) => {
        const user = data?.user;
        // Single source of truth — siehe /app/frontend/src/lib/licensing.js.
        const isPro = isPremium(user);
        return (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="mitgestalten-page">
            <motion.header
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]" data-testid="mitgestalten-eyebrow">
                Dein Beitrag
              </p>
              <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--fts-text-primary)]">
                🏛 Lass uns Flex4Success gemeinsam besser machen.
              </h1>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                Dein Feedback fließt direkt in die nächste Version. Wir antworten in der Regel innerhalb von 48 Stunden.
                {isPro && <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">Pro Solo · priorisiert</span>}
              </p>
            </motion.header>

            {submittedId && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="mitgestalten-success"
                className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
              >
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-emerald-900">Danke fürs Feedback!</p>
                  <p className="mt-0.5 text-xs text-emerald-800">
                    Dein Feedback wurde gespeichert. Referenz: <code className="font-mono bg-white dark:bg-slate-900 px-1 rounded">{submittedId}</code>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSubmittedId(null)}
                  className="text-emerald-700 hover:text-emerald-900"
                  aria-label="Bestätigung schließen"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {/* Submit-Form */}
            <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 sm:p-7 shadow-sm">
              {/* Kategorie */}
              <h2 className="text-sm font-extrabold text-[var(--fts-text-primary)] mb-3">
                Was möchtest du uns sagen?
              </h2>
              <div
                data-testid="mitgestalten-categories"
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                role="radiogroup"
                aria-label="Feedback-Kategorie"
              >
                {CATEGORIES.map(({ key, label, hint, Icon }) => {
                  const active = category === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setCategory(key)}
                      data-testid={`mitgestalten-cat-${key}`}
                      className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${
                        active ? "border-[#4563BB] bg-[#4563BB]/5" : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${active ? "text-[#4563BB]" : "text-slate-500"}`} />
                      <div>
                        <p className={`text-sm font-bold ${active ? "text-[#4563BB] dark:text-blue-300" : "text-slate-900 dark:text-white"}`}>{label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Message */}
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="mg-message" className="text-sm font-extrabold text-[var(--fts-text-primary)]">
                    Dein Feedback
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPreview(!showPreview)}
                      data-testid="mitgestalten-toggle-preview"
                      className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white underline"
                    >
                      {showPreview ? "Bearbeiten" : "Vorschau"}
                    </button>
                    <span
                      data-testid="mitgestalten-message-counter"
                      className={`text-xs ${messageLen > MAX_MESSAGE * 0.9 ? "text-amber-600 font-semibold" : "text-slate-400"}`}
                    >
                      {messageLen} / {MAX_MESSAGE}
                    </span>
                  </div>
                </div>
                {showPreview ? (
                  <div
                    data-testid="mitgestalten-preview"
                    className="min-h-[140px] rounded-md border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 dark:text-slate-100 prose-sm"
                    dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(message) || "<em style=\"color:#94A3B8;\">Vorschau erscheint, sobald du tippst.</em>" }}
                  />
                ) : (
                  <textarea
                    id="mg-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE))}
                    placeholder={
                      selectedCategory?.key === "bug"
                        ? "Was hat nicht funktioniert? Welche Schritte führten dazu?"
                        : selectedCategory?.key === "feature"
                        ? "Welches Feature wünschst du dir? Was würde es dir bringen?"
                        : "Beschreibe dein Anliegen so präzise wie möglich. Markdown wird unterstützt."
                    }
                    rows={6}
                    data-testid="mitgestalten-message"
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB] resize-y"
                  />
                )}
                <p className="mt-1 text-xs text-slate-400">
                  Markdown: <code>**fett**</code> · <code>*kursiv*</code> · <code>`code`</code> · <code>[link](url)</code> · <code>&gt; Zitat</code>
                </p>
              </div>

              {/* Screenshot */}
              <div className="mt-6">
                <label className="text-sm font-extrabold text-[var(--fts-text-primary)] block mb-2">
                  Screenshot (optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="mitgestalten-screenshot-input"
                />
                {screenshotB64 ? (
                  <div
                    data-testid="mitgestalten-screenshot-preview"
                    className="flex items-center gap-3 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40"
                  >
                    <ImageIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                    <span className="text-xs text-slate-700 dark:text-slate-300 flex-1 truncate">{screenshotName}</span>
                    <button
                      type="button"
                      onClick={() => { setScreenshotB64(null); setScreenshotName(""); }}
                      data-testid="mitgestalten-screenshot-remove"
                      className="text-slate-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="mitgestalten-screenshot-trigger"
                    className="flex items-center gap-2 w-full rounded-md border border-dashed border-slate-300 dark:border-slate-600 hover:border-[#4563BB] hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-3 text-sm text-slate-500 dark:text-slate-400"
                  >
                    <Upload className="w-4 h-4" />
                    Bild hochladen (max. 5 MB, PNG/JPG/GIF)
                  </button>
                )}
              </div>

              {/* Kontakt */}
              <div className="mt-6 rounded-xl bg-slate-50 dark:bg-slate-800/40 p-4">
                <p className="text-sm font-extrabold text-[var(--fts-text-primary)] mb-2">Kontakt (optional)</p>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={user?.email || "Email für Rückfragen"}
                  data-testid="mitgestalten-contact-email"
                  className="w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
                />
                <label className="mt-3 inline-flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notifyOnFix}
                    onChange={(e) => setNotifyOnFix(e.target.checked)}
                    data-testid="mitgestalten-notify-checkbox"
                    className="mt-0.5"
                  />
                  <span className="text-xs text-slate-700 dark:text-slate-300">
                    Benachrichtige mich, wenn mein Feedback umgesetzt wird.
                  </span>
                </label>
              </div>

              {error && (
                <p data-testid="mitgestalten-error" className="mt-4 text-sm text-red-600">
                  {error}
                </p>
              )}

              <div className="mt-6 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  data-testid="mitgestalten-cancel"
                  className="rounded-md border border-slate-200 dark:border-slate-700 px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!valid || submitting}
                  data-testid="mitgestalten-submit"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#4563BB] hover:bg-[#3a55a5] disabled:opacity-50 disabled:cursor-not-allowed text-white px-5 py-2 text-sm font-bold"
                >
                  {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Absenden
                </button>
              </div>
            </section>

            {/* Meine Feedbacks */}
            <section className="mt-8" data-testid="mitgestalten-mylist">
              <h2 className="flex items-center gap-2 text-base font-extrabold text-[var(--fts-text-primary)] mb-3">
                <FileText className="w-4 h-4 text-[#4563BB]" />
                Meine Feedbacks
              </h2>
              {myListLoading ? (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Lade…
                </p>
              ) : myList.length === 0 ? (
                <p
                  data-testid="mitgestalten-mylist-empty"
                  className="text-sm text-slate-500 italic"
                >
                  Du hast noch kein Feedback abgegeben.
                </p>
              ) : (
                <ul className="space-y-2">
                  {myList.map((item) => {
                    const status = STATUS_META[item.status] || STATUS_META.open;
                    const StatusIcon = status.Icon;
                    return (
                      <li
                        key={item.feedback_id}
                        data-testid={`mitgestalten-mylist-item-${item.feedback_id}`}
                        className="rounded-xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white line-clamp-2">
                              {item.message_preview}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {item.type} · {(item.created_at || "").slice(0, 10)} · {item.feedback_id}
                            </p>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${status.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        );
      }}
    </RedesignLayout>
  );
}
